import type { SimpleCitation } from './answers';

/** Where the backend retrieved a citation from: a present `retrieved_from`. */
export type RetrievedFrom = NonNullable<SimpleCitation['retrieved_from']>;

/**
 * True when at least one source came from an appliance's local corpus. Computed
 * once over the list a surface renders and handed to `getRetrievedFromLabel`.
 */
export function hasLocalRetrieval(
  sources: readonly Pick<SimpleCitation, 'retrieved_from'>[]
): boolean {
  return sources.some(
    (source) =>
      source.retrieved_from === 'journal_repo' || source.retrieved_from === 'library'
  );
}

/**
 * Which provenance label a source shows, or `null` for none (MOD-1726).
 *
 * - Absent → `null`: the cloud shape, rendered as it always has been.
 * - `journal_repo` / `library` → always labelled.
 * - `web` → labelled only beside a local source; when every source is web the
 *   label would say nothing the card does not already.
 * - A value this build does not know → `null` rather than a guess.
 *
 * Takes the wire value as a string so a newer backend cannot widen the label set.
 */
export function getRetrievedFromLabel(
  retrievedFrom: string | null | undefined,
  hasLocalSources: boolean
): RetrievedFrom | null {
  switch (retrievedFrom) {
    case 'journal_repo':
    case 'library':
      return retrievedFrom;
    case 'web':
      return hasLocalSources ? retrievedFrom : null;
    default:
      return null;
  }
}
